import { FC, memo, useCallback, useMemo } from 'react';

import useTooltipActions from '@/apps/mwd-v2/state/actions/useTooltipActions';
import { MousePointer } from '@/apps/mwd-v2/state/types/ActionStore';
import { preventDefault } from '@/apps/mwd-v2/utils/mwdEvents/eventHelpers';
import useClientState from '@/state/useClientState';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';

import { MwdOvergang } from '../../../types/Domain';

import styles from '../MwdChart.module.css';

type OvergangProps = {
  overgang: MwdOvergang;
  isAllowedToMutate: boolean;
  miniMwd?: boolean;
};

const Overgang: FC<OvergangProps> = ({ overgang, isAllowedToMutate, miniMwd }) => {
  const { setShowOvergangTooltip, hideTooltipWithTimeout } = useTooltipActions();

  const key = useMemo(() => {
    const altKey = `${overgang?.ketenConfig?.ketenIndex > 0 ? 'tussen_' : ''}overgang_${overgang.overgangId}_${overgang?.aankomstTijdVoorgaandeInzet?.toISOString() ?? overgang?.vertrekTijdVolgendeInzet?.toISOString()}`;
    return overgang.isOvergangVoorEersteInzet && overgang.vertrekTijdEersteInzet
      ? `overgang_${overgang.overgangId}_${overgang.vertrekTijdEersteInzet.toISOString()}`
      : altKey;
  }, [overgang]);

  const isSelected = useClientState((state) =>
    state.selected.overgangen.some((o) => o.overgangId === overgang.overgangId),
  );
  const selectOvergang = useClientState((state) => state.selectOvergang);
  const setShowOvergangContextMenu = useClientState((state) => state.setShowOvergangContextMenu);
  const setShowDialog = useClientState((state) => state.setShowDialog);

  const onContextMenuOvergang = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      if (!miniMwd) {
        void selectOvergang(overgang, event.ctrlKey, true);
        if (event.ctrlKey && event.button === 0) return preventDefault(event);
        void setShowOvergangContextMenu(overgang, localPoint(event) as MousePointer);
        return preventDefault(event);
      }
    },
    [overgang, miniMwd, selectOvergang, setShowOvergangContextMenu],
  );

  const onClickOvergang = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>) => {
      if (!miniMwd) {
        void selectOvergang(overgang, event.ctrlKey);
        return preventDefault(event);
      }
    },
    [overgang, miniMwd, selectOvergang],
  );

  const handleOnKeydownOvergang = useCallback(
    (event: React.KeyboardEvent) => {
      if (!miniMwd && isAllowedToMutate && event.altKey && event.code === 'KeyV') {
        event.preventDefault();
        event.stopPropagation();
        void setShowDialog('versterken', 'sneltoets');
      }
    },
    [isAllowedToMutate, miniMwd, setShowDialog],
  );

  const onMouseMove = useCallback(
    (event: React.MouseEvent) => {
      void setShowOvergangTooltip(
        {
          x: event.clientX,
          y: event.clientY,
        } as MousePointer,
        overgang,
      );
    },
    [setShowOvergangTooltip, overgang],
  );

  const onMouseLeave = useCallback(() => hideTooltipWithTimeout(), [hideTooltipWithTimeout]);

  return (
    <Group left={overgang.x}>
      <rect
        data-testid={`tussen-${overgang.overgangId}`}
        id={`tussen-${overgang.overgangId}`}
        width={overgang.width}
        tabIndex={0}
        x={-3} // -3 for smooth transition
        key={key}
        height={overgang.height} // 20 for correct height
        className={`${miniMwd ? styles.werklijnElementenStyleOvergangNoHover : styles.werklijnElementenStyleOvergang} ${isSelected ? 'is-selected-v2' : ''}`}
        onContextMenu={onContextMenuOvergang}
        onClick={onClickOvergang}
        y={overgang.y}
        data-is-overgang={true}
        onKeyDown={handleOnKeydownOvergang}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      />
    </Group>
  );
};
export default memo(Overgang);
